/**
 * Color utility functions for WCAG contrast validation and dark variant generation.
 *
 * Used by InPlaceColorEditor for accessibility warnings and by the partner
 * branding system to auto-derive dark mode colors from light mode primaries.
 */

// ─── Color Conversion Helpers ────────────────────────────────────────

/**
 * Parses a hex color string into RGB components.
 * Supports both 3-digit (#ABC) and 6-digit (#AABBCC) formats.
 */
export const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  const clean = hex.replace("#", "");
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(clean)) return null;

  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((c) => c + c)
          .join("")
      : clean;

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

/**
 * Converts RGB values to a hex color string.
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

/**
 * Converts RGB to HSL color space.
 * Returns h (0-360), s (0-100), l (0-100).
 */
export const rgbToHsl = (r: number, g: number, b: number): { h: number; s: number; l: number } => {
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;

  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const delta = max - min;

  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

    switch (max) {
      case rNorm:
        h = ((gNorm - bNorm) / delta + (gNorm < bNorm ? 6 : 0)) * 60;
        break;
      case gNorm:
        h = ((bNorm - rNorm) / delta + 2) * 60;
        break;
      case bNorm:
        h = ((rNorm - gNorm) / delta + 4) * 60;
        break;
    }
  }

  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
};

/**
 * Converts HSL back to RGB.
 * Expects h (0-360), s (0-100), l (0-100).
 */
export const hslToRgb = (h: number, s: number, l: number): { r: number; g: number; b: number } => {
  const sNorm = s / 100;
  const lNorm = l / 100;

  const c = (1 - Math.abs(2 * lNorm - 1)) * sNorm;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lNorm - c / 2;

  let r = 0,
    g = 0,
    b = 0;

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  };
};

// ─── WCAG Contrast Validation ────────────────────────────────────────

/**
 * Calculates relative luminance per WCAG 2.0 spec.
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
export const getRelativeLuminance = (hex: string): number => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const linearize = (channel: number): number => {
    const sRGB = channel / 255;
    return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * linearize(rgb.r) + 0.7152 * linearize(rgb.g) + 0.0722 * linearize(rgb.b);
};

/**
 * Calculates contrast ratio between two colors per WCAG 2.0.
 * Returns a value between 1 (identical) and 21 (black on white).
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const l1 = getRelativeLuminance(color1);
  const l2 = getRelativeLuminance(color2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

/** WCAG AA minimum contrast ratios */
export const WCAG_AA = {
  /** Normal text (< 18pt or < 14pt bold) */
  NORMAL_TEXT: 4.5,
  /** Large text (≥ 18pt or ≥ 14pt bold) and UI components */
  LARGE_TEXT: 3,
} as const;

export type WcagLevel = "pass" | "large-only" | "fail";

/**
 * Evaluates WCAG AA compliance for a foreground/background color pair.
 */
export const getWcagCompliance = (foreground: string, background: string): WcagLevel => {
  const ratio = getContrastRatio(foreground, background);
  if (ratio >= WCAG_AA.NORMAL_TEXT) return "pass";
  if (ratio >= WCAG_AA.LARGE_TEXT) return "large-only";
  return "fail";
};

/**
 * Context-aware contrast checks for a brand color.
 * Tests the color against the surfaces it will appear on in the app.
 */
export interface ContrastCheckResult {
  context: string;
  background: string;
  ratio: number;
  level: WcagLevel;
}

export const checkBrandContrast = (brandColor: string, mode: "light" | "dark"): ContrastCheckResult[] => {
  const surfaces =
    mode === "light"
      ? [
          { context: "White text on this color (buttons, AppBar)", background: "#FFFFFF" },
          { context: "This color as text on white", background: "#FFFFFF" },
          { context: "This color as text on light gray", background: "#F5F5F5" },
        ]
      : [
          { context: "Dark text on this color (buttons)", background: "#000000" },
          { context: "This color as text on dark surface", background: "#1E1E1E" },
          { context: "This color as text on dark background", background: "#121212" },
        ];

  return surfaces.map(({ context, background }) => {
    const ratio = getContrastRatio(brandColor, background);
    return {
      context,
      background,
      ratio: Math.round(ratio * 100) / 100,
      level: getWcagCompliance(brandColor, background),
    };
  });
};

// ─── Dark Variant Generation ─────────────────────────────────────────

/**
 * Generates a dark mode variant of a brand color.
 *
 * Strategy: Increase lightness to ensure visibility on dark backgrounds
 * while preserving the hue identity of the brand color. Also slightly
 * boosts saturation to compensate for perceived desaturation on dark surfaces.
 *
 * Target: The generated color should pass WCAG AA for large text (3:1)
 * against the dark mode paper surface (#1E1E1E).
 */
export const generateDarkVariant = (hex: string): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Start with increased lightness
  let targetL = Math.max(hsl.l, 55);
  // Slight saturation boost for dark backgrounds (capped at 100)
  const targetS = Math.min(hsl.s + 10, 100);

  // Iteratively increase lightness until we hit WCAG AA large text (3:1) on #1E1E1E
  const darkSurface = "#1E1E1E";
  let attempts = 0;

  while (attempts < 30) {
    const candidate = hslToRgb(hsl.h, targetS, targetL);
    const candidateHex = rgbToHex(candidate.r, candidate.g, candidate.b);
    const ratio = getContrastRatio(candidateHex, darkSurface);

    if (ratio >= WCAG_AA.LARGE_TEXT) return candidateHex;

    targetL += 2;
    attempts++;
  }

  // Fallback: return a reasonably light version
  const fallback = hslToRgb(hsl.h, targetS, 70);
  return rgbToHex(fallback.r, fallback.g, fallback.b);
};

/**
 * Generates both dark variants from light mode brand colors.
 */
export const generateDarkBranding = (
  brandPrimary: string,
  brandSecondary: string
): { brand_primary_dark: string; brand_secondary_dark: string } => ({
  brand_primary_dark: generateDarkVariant(brandPrimary),
  brand_secondary_dark: generateDarkVariant(brandSecondary),
});

// ─── Validation Helpers ──────────────────────────────────────────────

/**
 * Returns true if the string is a valid 6-digit hex color.
 */
export const isValidHexColor = (value: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(value);
